import differenceBy from 'lodash.differenceby';
import { absoluteMaxCapacityForDestination } from '../helpers/crew';
import { CREW, WEEK } from '../constants';
import {
  getSelectedDestination,
  getSelectedWeeks,
  getWeeksForYear,
  getSelectedCrew,
  getSelectedStandardYachtWeek,
  getSelectedParams,
  getAvailableCabinsForWeekAndCategory
} from '../helpers/week';

export const addNotification = newNotification => (dispatch, getState) => {
  const { notification } = getState();

  if (
    Object.keys(notification).length === 0 ||
    (notification && notification.priority <= newNotification.priority && notification.text !== newNotification.text)
  ) {
    dispatch({
      type: 'ADD_NOTIFICATION',
      notification: newNotification
    });
  }
};

export const removeNotification = uid => (dispatch, getState) => {
  const { notification } = getState();

  if (notification.uid === uid) {
    dispatch({
      type: 'REMOVE_NOTIFICATION',
      uid
    });
  }
};

export const shouldNotify = prevCrew => (dispatch, getState) => {
  const { destinations, yachts, cabins } = getState();
  const crew = getSelectedCrew(destinations.items, window.location);
  const {
    total, ratio, girls, boys, skipper, hostess
  } = crew;

  const guests = girls + boys;

  let selectedWeeks = getSelectedWeeks(destinations.items, window.location);

  const allWeeks = getWeeksForYear(destinations.items, window.location);

  // if the weeks are not selected we treat them as all selected
  if (selectedWeeks.length === 0) {
    selectedWeeks = allWeeks;
  }

  const weekDifference = differenceBy(allWeeks, selectedWeeks, 'week');
  const destination = getSelectedDestination(destinations.items, window.location);
  const badRatioWeeks = selectedWeeks.filter(week => week.ratio > ratio).map(week => week.week);

  const otherAvailableWeeks = weekDifference.filter(week => week.ratio <= ratio).map(week => week.week);

  const absoluteMaxCapacity = absoluteMaxCapacityForDestination(destination);

  const selectedStandardYachtWeek = getSelectedStandardYachtWeek(destinations.items, window.location);
  const maxGuests = selectedStandardYachtWeek && selectedStandardYachtWeek.standardYacht && selectedStandardYachtWeek.standardYacht.maxCapacity || CREW.MAX_GUESTS;

  /* REMOVE NOTIFICATION CRITERIA */
  if (total <= absoluteMaxCapacity && guests <= maxGuests) {
    dispatch(removeNotification(1));
  }

  if (skipper) {
    dispatch(removeNotification(2));
  }

  if (!hostess) {
    dispatch(removeNotification(3));
  }

  if (boys + girls >= 3) {
    dispatch(removeNotification(4));
  }

  if (badRatioWeeks.length !== selectedWeeks.length) {
    dispatch(removeNotification(6));
  }

  if (badRatioWeeks.length !== selectedWeeks.length || otherAvailableWeeks.length > 0) {
    dispatch(removeNotification(7));
  }

  if (badRatioWeeks.length === selectedWeeks.length || badRatioWeeks.length === 0) {
    dispatch(removeNotification(8));
  }

  if (yachts.filtered.length > 0) {
    dispatch(removeNotification(9));
  }

  if (boys + girls > 0) {
    dispatch(removeNotification(10));
  }

  /* Don't show notifications if destinations hasn't been selected */
  if (!destination) {
    return;
  }

  /* ADD NOTIFICATION CRITERIA */
  if (total > absoluteMaxCapacity || guests > maxGuests) {
    const newNotification = {
      uid: 1,
      type: 'error',
      priority: 10,
      title: '👨‍👩‍👧‍👦 Decrease your crew size',
      text: `Woah. Our yachts are big, but not that big.
        Please fill in your crew number to maximum of ${absoluteMaxCapacity} people (max ${maxGuests} guests).`
    };

    dispatch(addNotification(newNotification));
  }

  if (prevCrew.skipper && !skipper) {
    const newNotification = {
      uid: 2,
      type: 'warning',
      priority: 8,
      title: '⚓️ You’ve removed the skipper',
      text: `Sure you don’t need a skipper?
        You need a qualified sailor on board to be able to sail with us.
        Click below to see if you have a valid licence for your destination.`,
      link: '/wp/skipper-requirements'
    };

    dispatch(addNotification(newNotification));
  }

  if (
    !prevCrew.hostess && hostess && (
      !selectedStandardYachtWeek ||
      (selectedStandardYachtWeek.standardYacht && !selectedStandardYachtWeek.standardYacht.host)
    )
  ) {
    const hostessCost =
      getState().yachts.filtered.length > 0 ? getState().yachts.filtered[0].week.hostessCost / 100 : 700;
    const newNotification = {
      uid: 3,
      type: 'info',
      priority: 1,
      title: '💁🏼 You’ve added a host',
      text: `You're in for a treat!
        Prepare for delicious breakfasts and lunches served daily (+ x2 dinners a week).
        A host is €${hostessCost} for the week.
        Contributes towards achieving the ratio requirement.`
    };

    dispatch(addNotification(newNotification));

    // adding check for notification within timeout because can happen that it will be removed while waiting
    setTimeout(() => getState().notification.uid === 3 && dispatch(removeNotification(3)), 11000);
  }

  if (boys + girls > 0 && boys + girls <= 4) {

    const { category } = getSelectedParams(window.location);
    let cabinLink = '/browse/cabins/destinations';
    const cabinWeekStatus = selectedWeeks[0].cabinStatus;
    const cabinDataAvailability = getAvailableCabinsForWeekAndCategory(cabins.data, destinations, window.location, category, selectedWeeks[0]);
    const openCabins = cabinDataAvailability && Object.entries(cabinDataAvailability.cabin).some(item => item[1].status === WEEK.OPEN_STATUS); // Object.entries splits object children into arrays of key values [[key, value], [key, value]]. (item[0] is boy, girl or mixed, item[1] is that cabin data)
    const numberOfCabinsAvailable = selectedWeeks[0]?.available?.[category]?.cabin;
    let cabinsAvailable = false;
    if (numberOfCabinsAvailable === null && openCabins) {
      cabinsAvailable = true;
    } else if (numberOfCabinsAvailable >= (boys + girls / 2)) {
      cabinsAvailable = true;
    }
    if (destination && destination.cabinStatus === WEEK.OPEN_STATUS && cabinWeekStatus === WEEK.OPEN_STATUS && cabinDataAvailability && cabinsAvailable) {
      cabinLink = `/browse/cabins/crew?category=${category}&dest=${destination.id}&weeks=${selectedWeeks[0].week}&year=${selectedWeeks[0].year}`;
      const newNotification = {
        uid: 4,
        type: 'info',
        priority: 11,
        title: 'Your group can\'t fill a whole yacht?',
        text: 'We have 2-person cabins available. You can book multiple cabins on the same yacht.',
        cta: { link: cabinLink, text: 'See cabins' }
      };
      dispatch(addNotification(newNotification));
    }

  }

  if (!prevCrew.skipper && !prevCrew.hostess && hostess) {
    const newNotification = {
      uid: 5,
      type: 'warning',
      priority: 6,
      title: '🙅🏼 No host without a skipper',
      text: 'Sorry sailor, hosts are only an option if you have a TYW skipper on board.'
    };

    dispatch(addNotification(newNotification));

    setTimeout(() => dispatch(removeNotification(5)), 5000);
  }

  if (
    selectedWeeks &&
    selectedWeeks.length &&
    badRatioWeeks.length === selectedWeeks.length &&
    otherAvailableWeeks.length > 0
  ) {
    const newNotification = {
      uid: 6,
      type: 'error',
      priority: 8,
      title:
        selectedWeeks.length === 1 ? "Selected week doesn't suit your ratio" : 'Selected weeks don’t suit your ratio',
      text: `You can't browse yachts for selected week${
        selectedWeeks.length === 1 ? '' : 's'
      } with your gender ratio. ${
        hostess ? 'Consider changing your weeks' : 'Consider adding a host (host counts as a girl) or change your weeks'
      } (other available weeks: ${otherAvailableWeeks.join(', ')}).`
    };

    dispatch(addNotification(newNotification));
  }

  if (badRatioWeeks.length === selectedWeeks.length && otherAvailableWeeks.length === 0) {
    const newNotification = {
      uid: 7,
      type: 'error',
      priority: 9,
      title: '🚫 No weeks suit your ratio',
      text: `Balance your gender ratio to browse yachts. ${
        !hostess ? 'Consider adding a host, host counts as a girl.' : ''
      }`
    };

    dispatch(addNotification(newNotification));
  }

  if (badRatioWeeks.length !== selectedWeeks.length && badRatioWeeks.length !== 0) {
    const newNotification = {
      uid: 8,
      type: 'warning',
      priority: 5,
      title: '💔 Your gender ratio is limiting your search',
      text: `Balance your gender ratio to view week ${badRatioWeeks.join(', ')}. ${
        !hostess ? 'Consider adding a host, host counts as a girl.' : ''
      }`
    };

    dispatch(addNotification(newNotification));
  }

  if (boys + girls === 0) {
    const newNotification = {
      uid: 10,
      type: 'error',
      priority: 9,
      title: "🙈 Errm. You can't sail zero people",
      text: "Add yourself and some friends and we'll all be happy"
    };

    dispatch(addNotification(newNotification));
  }
};
